import React, { useState, useEffect } from 'react';
import { User } from '../../types';
import { NIGERIAN_STATES, getLGAsForState } from '../../data/nigerianData';

interface PersonalInfoData {
  firstName: string;
  lastName: string;
  middleName: string;
  dateOfBirth: string;
  gender: string;
  maritalStatus: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  lga: string;
  nextOfKinName: string;
  nextOfKinPhone: string;
  nextOfKinRelationship: string;
}

interface PersonalInfoStepProps {
  user: User;
  initialData?: Partial<PersonalInfoData>;
  onNext: (data: PersonalInfoData) => void;
  onBack?: () => void;
}

export const PersonalInfoStep: React.FC<PersonalInfoStepProps> = ({
  user,
  initialData,
  onNext,
  onBack,
}) => {
  const [formData, setFormData] = useState<PersonalInfoData>({
    firstName: user.first_name || '',
    lastName: user.last_name || '',
    middleName: user.middle_name || '',
    dateOfBirth: user.date_of_birth || '',
    gender: user.gender || '',
    maritalStatus: user.marital_status || '',
    phone: user.phone || '',
    email: user.email || '',
    address: initialData?.address || '',
    city: initialData?.city || '',
    state: initialData?.state || '',
    lga: initialData?.lga || '',
    nextOfKinName: initialData?.nextOfKinName || '',
    nextOfKinPhone: initialData?.nextOfKinPhone || '',
    nextOfKinRelationship: initialData?.nextOfKinRelationship || '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof PersonalInfoData, string>>>({});
  const [touchedFields, setTouchedFields] = useState<Set<keyof PersonalInfoData>>(new Set());

  const [availableLGAs, setAvailableLGAs] = useState<string[]>([]);

  useEffect(() => {
    if (formData.state) {
      const lgas = getLGAsForState(formData.state);
      setAvailableLGAs(lgas);
      // Reset LGA if state changed and current LGA is not in the new list
      if (formData.lga && !lgas.includes(formData.lga)) {
        setFormData((prev) => ({ ...prev, lga: '' }));
      }
    } else {
      setAvailableLGAs([]);
    }
  }, [formData.state, formData.lga]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name as keyof PersonalInfoData]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (field: keyof PersonalInfoData) => {
    setTouchedFields((prev) => new Set(prev).add(field));
    validateField(field);
  };

  const validateField = (field: keyof PersonalInfoData): string => {
    const value = formData[field];
    let error = '';

    switch (field) {
      case 'dateOfBirth':
        if (!value) {
          error = 'Date of birth is required';
        } else {
          const dob = new Date(value);
          const today = new Date();
          const age = today.getFullYear() - dob.getFullYear();
          if (age < 18) {
            error = 'You must be at least 18 years old';
          } else if (age > 100) {
            error = 'Please enter a valid date of birth';
          }
        }
        break;

      case 'gender':
        if (!value) error = 'Gender is required';
        break;

      case 'maritalStatus':
        if (!value) error = 'Marital status is required';
        break;

      case 'address':
        if (!value || value.trim().length < 10) {
          error = 'Please enter a complete address (at least 10 characters)';
        }
        break;

      case 'city':
        if (!value) error = 'City is required';
        break;

      case 'state':
        if (!value) error = 'State is required';
        break;

      case 'lga':
        if (!value) error = 'Local Government Area (LGA) is required';
        break;

      case 'nextOfKinName':
        if (!value || value.trim().length < 3) {
          error = 'Next of kin name is required';
        }
        break;

      case 'nextOfKinPhone':
        if (!value) {
          error = 'Next of kin phone number is required';
        } else if (!/^(\+234|0)[789]\d{9}$/.test(value.replace(/[\s\-()]/g, ''))) {
          error = 'Please enter a valid Nigerian phone number';
        }
        break;

      case 'nextOfKinRelationship':
        if (!value) error = 'Next of kin relationship is required';
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  };

  const validateAll = (): boolean => {
    const fieldsToValidate: (keyof PersonalInfoData)[] = [
      'dateOfBirth',
      'gender',
      'maritalStatus',
      'address',
      'city',
      'state',
      'lga',
      'nextOfKinName',
      'nextOfKinPhone',
      'nextOfKinRelationship',
    ];

    const newErrors: Partial<Record<keyof PersonalInfoData, string>> = {};
    let hasErrors = false;

    fieldsToValidate.forEach((field) => {
      const error = validateField(field);
      if (error) {
        newErrors[field] = error;
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    setTouchedFields(new Set(fieldsToValidate));

    return !hasErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateAll()) {
      onNext(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="application-form">
      <div className="form-section">
        <h3>Personal Information</h3>
        <p className="section-description">
          Please provide your personal information. Fields marked with * are required.
        </p>

        {/* Name fields - read-only */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              readOnly
              disabled
              className="read-only"
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              readOnly
              disabled
              className="read-only"
            />
          </div>

          <div className="form-group">
            <label htmlFor="middleName">Middle Name</label>
            <input
              type="text"
              id="middleName"
              name="middleName"
              value={formData.middleName}
              readOnly
              disabled
              className="read-only"
            />
          </div>
        </div>

        {/* Contact info - read-only */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              readOnly
              disabled
              className="read-only"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              readOnly
              disabled
              className="read-only"
            />
          </div>
        </div>

        {/* Personal details */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="dateOfBirth">
              Date of Birth *
            </label>
            <input
              type="date"
              id="dateOfBirth"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
              onBlur={() => handleBlur('dateOfBirth')}
              max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
              required
              className={touchedFields.has('dateOfBirth') && errors.dateOfBirth ? 'error' : ''}
            />
            {touchedFields.has('dateOfBirth') && errors.dateOfBirth && (
              <span className="field-error">{errors.dateOfBirth}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="gender">Gender *</label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              onBlur={() => handleBlur('gender')}
              required
              className={touchedFields.has('gender') && errors.gender ? 'error' : ''}
            >
              <option value="">Select gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </select>
            {touchedFields.has('gender') && errors.gender && (
              <span className="field-error">{errors.gender}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="maritalStatus">Marital Status *</label>
            <select
              id="maritalStatus"
              name="maritalStatus"
              value={formData.maritalStatus}
              onChange={handleChange}
              onBlur={() => handleBlur('maritalStatus')}
              required
              className={touchedFields.has('maritalStatus') && errors.maritalStatus ? 'error' : ''}
            >
              <option value="">Select marital status</option>
              <option value="Single">Single</option>
              <option value="Married">Married</option>
              <option value="Divorced">Divorced</option>
              <option value="Widowed">Widowed</option>
            </select>
            {touchedFields.has('maritalStatus') && errors.maritalStatus && (
              <span className="field-error">{errors.maritalStatus}</span>
            )}
          </div>
        </div>

        {/* Address */}
        <div className="form-group">
          <label htmlFor="address">Residential Address *</label>
          <textarea
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            onBlur={() => handleBlur('address')}
            rows={3}
            required
            placeholder="Enter your complete residential address"
            className={touchedFields.has('address') && errors.address ? 'error' : ''}
          />
          {touchedFields.has('address') && errors.address && (
            <span className="field-error">{errors.address}</span>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="city">City *</label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              onBlur={() => handleBlur('city')}
              required
              placeholder="Enter city"
              className={touchedFields.has('city') && errors.city ? 'error' : ''}
            />
            {touchedFields.has('city') && errors.city && (
              <span className="field-error">{errors.city}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="state">State *</label>
            <select
              id="state"
              name="state"
              value={formData.state}
              onChange={handleChange}
              onBlur={() => handleBlur('state')}
              required
              className={touchedFields.has('state') && errors.state ? 'error' : ''}
            >
              <option value="">Select state</option>
              {NIGERIAN_STATES.map((state) => (
                <option key={state.name} value={state.name}>
                  {state.name}
                </option>
              ))}
            </select>
            {touchedFields.has('state') && errors.state && (
              <span className="field-error">{errors.state}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="lga">Local Government Area (LGA) *</label>
            <select
              id="lga"
              name="lga"
              value={formData.lga}
              onChange={handleChange}
              onBlur={() => handleBlur('lga')}
              required
              disabled={!formData.state || availableLGAs.length === 0}
              className={touchedFields.has('lga') && errors.lga ? 'error' : ''}
            >
              <option value="">
                {formData.state ? 'Select LGA' : 'Select state first'}
              </option>
              {availableLGAs.map((lga) => (
                <option key={lga} value={lga}>
                  {lga}
                </option>
              ))}
            </select>
            {touchedFields.has('lga') && errors.lga && (
              <span className="field-error">{errors.lga}</span>
            )}
            {formData.state && availableLGAs.length === 0 && (
              <span className="form-hint">No LGAs available for selected state</span>
            )}
          </div>
        </div>
      </div>

      {/* Next of Kin Information */}
      <div className="form-section">
        <h3>Next of Kin Information</h3>
        <p className="section-description">
          Please provide details of your next of kin for emergency contact purposes.
        </p>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="nextOfKinName">Next of Kin Full Name *</label>
            <input
              type="text"
              id="nextOfKinName"
              name="nextOfKinName"
              value={formData.nextOfKinName}
              onChange={handleChange}
              onBlur={() => handleBlur('nextOfKinName')}
              required
              placeholder="Enter full name"
              className={touchedFields.has('nextOfKinName') && errors.nextOfKinName ? 'error' : ''}
            />
            {touchedFields.has('nextOfKinName') && errors.nextOfKinName && (
              <span className="field-error">{errors.nextOfKinName}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="nextOfKinPhone">Next of Kin Phone Number *</label>
            <input
              type="tel"
              id="nextOfKinPhone"
              name="nextOfKinPhone"
              value={formData.nextOfKinPhone}
              onChange={handleChange}
              onBlur={() => handleBlur('nextOfKinPhone')}
              required
              placeholder="+234 801 234 5678"
              className={touchedFields.has('nextOfKinPhone') && errors.nextOfKinPhone ? 'error' : ''}
            />
            {touchedFields.has('nextOfKinPhone') && errors.nextOfKinPhone && (
              <span className="field-error">{errors.nextOfKinPhone}</span>
            )}
            <span className="form-hint">Enter Nigerian phone number</span>
          </div>

          <div className="form-group">
            <label htmlFor="nextOfKinRelationship">Relationship *</label>
            <select
              id="nextOfKinRelationship"
              name="nextOfKinRelationship"
              value={formData.nextOfKinRelationship}
              onChange={handleChange}
              onBlur={() => handleBlur('nextOfKinRelationship')}
              required
              className={touchedFields.has('nextOfKinRelationship') && errors.nextOfKinRelationship ? 'error' : ''}
            >
              <option value="">Select relationship</option>
              <option value="Spouse">Spouse</option>
              <option value="Parent">Parent</option>
              <option value="Sibling">Sibling</option>
              <option value="Child">Child</option>
              <option value="Friend">Friend</option>
              <option value="Other">Other</option>
            </select>
            {touchedFields.has('nextOfKinRelationship') && errors.nextOfKinRelationship && (
              <span className="field-error">{errors.nextOfKinRelationship}</span>
            )}
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        {onBack && (
          <button type="button" onClick={onBack} className="btn btn-secondary">
            Back
          </button>
        )}
        <button type="submit" className="btn btn-primary">
          Continue to Employment Information
        </button>
      </div>
    </form>
  );
};
